// The locked screen — `todo-takeout.md` entry 6.25.
//
// Key export: WatchLayout.
// Neighbours: shell/AppRoutes.tsx (renders this tree instead of the ordinary
// one while locked), state/useWatchMode.ts (the lock), lib/serverFetch.ts
// (the refusal that makes this more than a hidden nav).
//
// **One panel, full screen, and no way out but the passphrase.** No sidebar,
// no mobile nav, no brand header, no restaurant switcher: what a passer-by can
// see is the panel the operator left up, and what they can reach is nothing.
//
// **The redirect is the lock's routing half.** A typed URL, a restored tab, a
// bookmark and the back button all arrive here, and all of them land back on
// the locked panel. Hiding the navigation would leave every one of those open.

import { useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { Lock } from 'lucide-react'

import { useWatchMode } from '@/state/useWatchMode'
import { PassphrasePrompt } from '@/components/PassphrasePrompt'

export function WatchLayout() {
  const lock = useWatchMode((s) => s.lock)
  const stopWatching = useWatchMode((s) => s.stopWatching)
  const { pathname, search } = useLocation()
  const [asking, setAsking] = useState(false)

  // The tree this layout belongs to is only rendered while locked, so this is
  // a guard against a lock released mid-render rather than the ordinary path.
  if (!lock) return <Navigate to={pathname} replace />

  // **Compared without the query string.** The board's own date and tab live
  // there, and a redirect that reset them every time a merchant changed the
  // service day would make the locked panel unusable — which is the state the
  // booth was in.
  if (pathname !== lock.panel) {
    return <Navigate to={lock.panel} replace />
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <main className="flex-1 overflow-auto" key={search}>
        <Outlet />
      </main>

      {/* Bottom right, per the entry. Small and unlabelled-looking on purpose:
          it is for the person who locked the screen, not an invitation to the
          queue. */}
      <button
        type="button"
        onClick={() => setAsking(true)}
        aria-label="Unlock this screen"
        className="fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground shadow-lg transition-colors hover:text-foreground"
      >
        <Lock className="h-4 w-4" aria-hidden />
        Watching
      </button>

      <PassphrasePrompt
        open={asking}
        title="Unlock this screen"
        body="Enter the passphrase set when this screen was locked."
        confirmLabel="Unlock"
        onCancel={() => setAsking(false)}
        onSubmit={async (passphrase) => {
          const opened = await stopWatching(passphrase)
          if (opened) setAsking(false)
          return opened ? null : 'That is not the passphrase for this screen.'
        }}
      />
    </div>
  )
}
